/**
 * shared/rooms.ts — the room-type catalog + the room vocabulary.
 *
 * A ROOM TYPE (rooms.store, one of the repo-root stores) is a "room
 * algorithm": the floor tile shade (a palettes.json palette) + the
 * obstacle placement rules. The generator (gen/generate.ts) uses the
 * recipe to furnish every non-base room deterministically (sim/furnish.ts,
 * the 'furnish' RNG fork); the client resolves the floor tile from the
 * same catalog (floorId -> roomId -> floorTile — no wire field needed);
 * the arena and the editors list the same catalog.
 *
 * `ROOMS_DATA` is generated from rooms.store (tools/room-store.js) —
 * never edit it. This module validates it at build time (a bad store is a
 * build error, like the other catalogs) and keeps the fixed vocabularies
 * here, where the sim (name -> terrain id), the store validator
 * (tools/room-store.js keeps its copy in sync) and the editors all read
 * the same list.
 */
import { ROOMS_DATA } from "./rooms.data.js";

export const TERRAIN_FLOOR = 0;
export const TERRAIN_WALL = 1;
export const TERRAIN_POND = 2;
export const TERRAIN_TABLE = 3;

/** The fixed obstacle art vocabulary (rooms.store rules + the client's
 *  worldArt lookups key off these names). A new type = pipeline art +
 *  this list + tools/room-store.js's copy. */
export const OBSTACLE_TYPES = ["wall", "pond", "table"] as const;
export type ObstacleType = (typeof OBSTACLE_TYPES)[number];

/** Placement algorithms (sim/furnish.ts implements all three):
 *  scatter = N distinct candidate tiles; line = a straight N-tile run
 *  (random orientation + position); cluster = a compact blob grown
 *  4-adjacent from a seed tile. */
export const OBSTACLE_ALGOS = ["scatter", "line", "cluster"] as const;
export type ObstacleAlgo = (typeof OBSTACLE_ALGOS)[number];

/** Sight class: `low` blocks walking only (shots pass over — kiting
 *  geometry); `tall` blocks walking AND sight (cover). The dev's
 *  per-rule decision — both are implemented, the store picks. */
export const SIGHT_CLASSES = ["low", "tall"] as const;
export type SightClass = (typeof SIGHT_CLASSES)[number];

/** Client-only room rendering choices; excluded from RoomRecipe on purpose. */
export const ARCHITECTURES = ["legacy", "civic-stone-v1"] as const;
export type Architecture = (typeof ARCHITECTURES)[number];

/** The rule's terrain id (what lands in State.terrain). */
export const obstacleTerrain = (type: ObstacleType): number => {
  switch (type) {
    case "wall":
      return TERRAIN_WALL;
    case "pond":
      return TERRAIN_POND;
    case "table":
      return TERRAIN_TABLE;
  }
};

export interface ObstacleRule {
  type: ObstacleType;
  algo: ObstacleAlgo;
  /** min obstacle count per 10x10 room (inclusive). */
  min: number;
  /** max obstacle count per 10x10 room (inclusive). */
  max: number;
  sight: SightClass;
}

/** The room type as stored (rooms.store projection). */
export interface RoomTypeDef {
  id: number;
  name: string;
  /** The floor tile shade — a palettes.json palette name; the atlas
   *  entry is `floor-<floorTile>` (generated for every palette). */
  floorTile: string;
  architecture: Architecture;
  wear: number;
  obstacles: ObstacleRule[];
}

/** The recipe the sim/generator consumes (the playable fields of a room
 *  type — no store id/name). */
export interface RoomRecipe {
  floorTile: string;
  obstacles: ObstacleRule[];
}

export const roomRecipe = (room: RoomTypeDef): RoomRecipe => ({
  floorTile: room.floorTile,
  obstacles: room.obstacles.map((r) => ({ ...r })),
});

// ---- build-time validation (a bad store is a build error) ---------------

const isRule = (r: unknown): r is ObstacleRule => {
  const o = r as ObstacleRule;
  return (
    !!o &&
    OBSTACLE_TYPES.includes(o.type) &&
    OBSTACLE_ALGOS.includes(o.algo) &&
    SIGHT_CLASSES.includes(o.sight) &&
    Number.isInteger(o.min) &&
    Number.isInteger(o.max) &&
    o.min >= 0 &&
    o.min <= o.max
  );
};

const isRoom = (f: unknown): f is RoomTypeDef => {
  const o = f as RoomTypeDef;
  return (
    !!o &&
    Number.isInteger(o.id) &&
    o.id >= 0 &&
    typeof o.name === "string" &&
    o.name.length > 0 &&
    typeof o.floorTile === "string" &&
    o.floorTile.length > 0 &&
    ARCHITECTURES.includes(o.architecture) &&
    Number.isInteger(o.wear) &&
    o.wear >= 0 &&
    o.wear <= 100 &&
    Array.isArray(o.obstacles) &&
    o.obstacles.every(isRule) &&
    new Set(o.obstacles.map((r) => r.type)).size === o.obstacles.length
  );
};

export const ROOMS: readonly RoomTypeDef[] = (() => {
  const rooms = ROOMS_DATA as unknown as readonly RoomTypeDef[];
  if (!rooms.every(isRoom)) throw new Error("invalid rooms catalog (rooms.data.ts)");
  const seen = new Set<number>();
  for (const f of rooms) {
    if (seen.has(f.id)) throw new Error(`duplicate room id ${f.id} (rooms.data.ts)`);
    seen.add(f.id);
  }
  return rooms;
})();

export const roomById = (id: number): RoomTypeDef | null =>
  ROOMS.find((f) => f.id === id) ?? null;
